"use client";

import { c as _c } from "react/compiler-runtime";
import { Typography, TextButton, Icon } from "@/shared/ui/atoms";
import { CustomButton, ChannelIcon } from "@/shared/ui/molecules";
import { useRef, useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { formatDate, formatDateAttribute } from "@/shared/utils/format";
import { maskAuthor } from "@/shared/utils/format";
import { resolveReviewTranslationKey } from "@/entities/hospital/lib/review-translation";
import { resolveHospitalReviewDate } from "@/entities/hospital/lib/review-date";
import { useGetLocale } from "@/shared/hooks/useGetLocale";
import { renderTextWithMatchHighlights } from "@/shared/utils/matchHighlight";
import type { HospitalReview } from "@/shared/types/type/hospital";
import { Channel } from "@/shared/types/type/review";
import { formatFivePointRating } from "@/entities/hospital/lib/rating";

/** API 리뷰 아이템 + UI가 필요로 하는(레거시) optional 필드 */
export type ReviewProps = HospitalReview & {
  /**
   * 레거시(과거) 필드. 번역/원문 토글이 `translatedContents`로 대체되기 전 데이터 호환용.
   */
  originalContent?: string;
};
interface ReviewMoleculeProps {
  review: ReviewProps;
}
const Review = t0 => {
  const $ = _c(72);
  const {
    review
  } = t0;
  const t = useTranslations();
  const locale = useGetLocale();
  const [isOpen, setIsOpen] = useState(false);
  const [isMore, setIsMore] = useState(false);
  const [isShowMoreButton, setIsShowMoreButton] = useState(false);
  const textRef = useRef(null);
  const {
    score,
    author,
    content,
    originalContent,
    translatedContents,
    matchContents,
    channelId
  } = review;
  const tr = translatedContents;
  let t1;
  if ($[0] !== locale) {
    t1 = resolveReviewTranslationKey(locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const localeKey = t1;
  const fromTranslated = tr?.[localeKey]?.trim();
  const displayText = (fromTranslated && fromTranslated.length > 0 ? fromTranslated : content).trim();
  let t2;
  if ($[2] !== tr?.ko) {
    t2 = tr?.ko?.trim();
    $[2] = tr?.ko;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const koreanFromTranslated = t2;
  const t3 = koreanFromTranslated && koreanFromTranslated.length > 0 ? koreanFromTranslated : content;
  let t4;
  if ($[4] !== t3) {
    t4 = t3.trim();
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const korean = t4;
  const hasNonKoTranslation = Boolean(tr) && localeKey !== "ko" && Boolean(fromTranslated && fromTranslated.length > 0) && Boolean(korean && korean.length > 0);
  const legacyHasOriginal = originalContent != null && originalContent !== content;
  const showToggle = hasNonKoTranslation || legacyHasOriginal;
  const originalText = (hasNonKoTranslation ? korean : undefined) ?? (legacyHasOriginal ? originalContent : undefined);
  const showOriginalToggle = Boolean(showToggle && originalText != null && originalText.length > 0);
  let t5;
  if ($[6] !== matchContents) {
    t5 = matchContents?.some(_temp);
    $[6] = matchContents;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const shouldHighlight = Boolean(t5);
  let formattedDate;
  let t6;
  if ($[8] !== review) {
    const reviewDate = resolveHospitalReviewDate(review);
    formattedDate = reviewDate ? formatDate(reviewDate) : undefined;
    t6 = reviewDate ? formatDateAttribute(reviewDate) : null;
    $[8] = review;
    $[9] = formattedDate;
    $[10] = t6;
  } else {
    formattedDate = $[9];
    t6 = $[10];
  }
  const dateAttribute = t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!textRef.current) {
        return;
      }
      if (textRef.current.scrollHeight > textRef.current.clientHeight) {
        setIsShowMoreButton(true);
      } else {
        setIsShowMoreButton(false);
      }
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const checkShowMoreButton = t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const timer = setTimeout(() => {
        checkShowMoreButton();
      }, 0);
      window.addEventListener("resize", checkShowMoreButton);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", checkShowMoreButton);
      };
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useEffect(t8, [content, displayText, shouldHighlight]);
  const t9 = "py-5 px-4 flex flex-col border-b border-[#F4F4F5]";
  const t10 = channelId as unknown as Channel;
  let t11;
  if ($[13] !== t10) {
    t11 = <ChannelIcon channel={t10} />;
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Icon src="/icons/star.svg" width={20} height={20} alt="star" />;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = Number(score);
  let t14;
  if ($[16] !== t13) {
    t14 = formatFivePointRating(t13);
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== t14) {
    t15 = <Typography className="ml-[2px] text-[#333438 font-bold">{t14}</Typography>;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="mx-[6px] w-[3px] h-[3px] bg-gray-300 rounded-full" />;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== author) {
    t17 = maskAuthor(author);
    $[21] = author;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t17) {
    t18 = <Typography size={12} lineHeight="normal" className="text-[#C2C4C8]"><span suppressHydrationWarning={true}>{t17}</span></Typography>;
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== channelId || $[26] !== t) {
    t19 = channelId === Channel.REVIEW_BOOST && <><span className="mx-[6px] w-[3px] h-[3px] bg-gray-300 rounded-full" /><Typography size={12} lineHeight="normal" className="text-[#C2C4C8]"><span suppressHydrationWarning={true}>{t("commonReview.visitReview")}</span></Typography></>;
    $[25] = channelId;
    $[26] = t;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t11 || $[29] !== t15 || $[30] !== t18 || $[31] !== t19) {
    t20 = <div className="flex items-center">{t11}{t12}{t15}{t16}{t18}{t19}</div>;
    $[28] = t11;
    $[29] = t15;
    $[30] = t18;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== dateAttribute || $[34] !== formattedDate) {
    t21 = dateAttribute ? <time dateTime={dateAttribute}>{formattedDate}</time> : formattedDate;
    $[33] = dateAttribute;
    $[34] = formattedDate;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t21) {
    t22 = <Typography size={12} lineHeight="normal" className="text-[#C2C4C8]">{t21}</Typography>;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t20 || $[39] !== t22) {
    t23 = <div className="mb-2.5 flex items-start justify-between gap-2">{t20}{t22}</div>;
    $[38] = t20;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== channelId || $[42] !== t) {
    t24 = channelId === Channel.REVIEW_BOOST && <div className="mb-1.5 w-fit flex items-center gap-[5px] py-1 px-2 bg-gray-100 rounded-full"><Icon src="/icons/verified-icon.png" width={16} height={16} alt="verified" /><Typography size={12} lineHeight="normal" className="text-gray-700 font-semibold">{t("commonReview.verifiedReceipt")}</Typography></div>;
    $[41] = channelId;
    $[42] = t;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  const t25 = "relative mb-2.5 pl-1";
  const T0 = Typography;
  const t26 = 15;
  const t27 = `text-overflow-ellipsis ${isMore ? "line-clamp-none" : "line-clamp-3"}`;
  const t28 = shouldHighlight ? renderTextWithMatchHighlights(displayText, matchContents) : displayText;
  let t29;
  if ($[44] !== T0 || $[45] !== t27 || $[46] !== t28) {
    t29 = <div className={t25}><T0 ref={textRef} size={t26} className={t27}>{t28}</T0></div>;
    $[44] = T0;
    $[45] = t27;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== isOpen || $[49] !== originalText || $[50] !== showOriginalToggle || $[51] !== t) {
    t30 = showOriginalToggle && originalText && <CustomButton onClick={() => {
      setIsOpen(!isOpen);
    }} className="w-fit py-1 px-2 flex items-center gap-1 border border-[#EAEBEC] rounded-lg"><Typography size={12} lineHeight={18} className="text-[#5A5C63]">{t("commonReview.viewOriginal")}</Typography><Icon src="/icons/transfer.svg" width={16} height={16} alt="transfer" /></CustomButton>;
    $[48] = isOpen;
    $[49] = originalText;
    $[50] = showOriginalToggle;
    $[51] = t;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== isMore || $[54] !== isShowMoreButton || $[55] !== t) {
    t31 = !isMore && isShowMoreButton && <TextButton text={t("commonReview.more")} onClick={() => {
      setIsMore(!isMore);
    }} size={14} className="absolute right-0 top-1/2 -translate-y-1/2 text-[#5A5C63] leading-[21px] underline decoration-solid" />;
    $[53] = isMore;
    $[54] = isShowMoreButton;
    $[55] = t;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== t30 || $[58] !== t31) {
    t32 = <div className="relative pl-1">{t30}{t31}</div>;
    $[57] = t30;
    $[58] = t31;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] !== isOpen || $[61] !== matchContents || $[62] !== originalText || $[63] !== shouldHighlight || $[64] !== showOriginalToggle) {
    t33 = isOpen && showOriginalToggle && originalText && <Typography size={15}>{shouldHighlight ? renderTextWithMatchHighlights(originalText, matchContents) : originalText}</Typography>;
    $[60] = isOpen;
    $[61] = matchContents;
    $[62] = originalText;
    $[63] = shouldHighlight;
    $[64] = showOriginalToggle;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] !== t23 || $[67] !== t24 || $[68] !== t29 || $[69] !== t32 || $[70] !== t33) {
    t34 = <div className={t9}>{t23}{t24}{t29}{t32}{t33}</div>;
    $[66] = t23;
    $[67] = t24;
    $[68] = t29;
    $[69] = t32;
    $[70] = t33;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  return t34;
};
export default Review;
function _temp(m) {
  return m.trim().length > 0;
}